/* eslint-disable no-debugger */
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "@usas/ui-web/wrappers/ReactRouterDom";

import NavLinks from "../../NavLinks";

import useClubMeetsData from "../../../../state/clubMeets/UseClubMeetsData";
import useUsasClubFacilityLinksData from '../../../../state/clubFacilityLinks/UseUsasClubFacilityLinksData';

import { navigateToCrossUiRoute } from '@usas/ui-web/utils/FormatCrossUiRoute';
import useMeetSessionsData from '@usas/ui-web/state/meetSessions/UseMeetSessionsData';
import useBaseUsasClubData from "@usas/ui-web/state/clubBase/UseBaseUsasClubData";
import useEnvironmentVariableData from "@usas/ui-web/state/environmentVariable/UseEnvironmentVariableData";
import useGlobalRoutesData from '@usas/ui-web/state/security/UseGlobalRoutesData';
import useLscData from '@usas/ui-web/state/lsc/UseLscData';
import useMeetData from '@usas/ui-web/state/meet/UseMeetNoContextData';
import useSecurityData from '@usas/ui-web/state/security/UseSecurityData';
import UseForm from "@usas/ui-web/utils/UseForm";
import validate from "./forms/MeetApprovedInfoFormValidation";
import Constants from "@usas/ui-web/utils/Constants";
import { formatDate } from "@usas/ui-web/utils/DateFunctions";

const UseClubMeetApprovedRequest = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { environmentVariableState, OFFICIAL_ORG_ROLE_ID, OFFICIALS_ROLES_ROLE_GROUP_ID, USAS_ORG_UNIT_ID } = useEnvironmentVariableData();
  const { baseUsasClubState } = useBaseUsasClubData();
  const { usasClubFacilityLinksState, getUsasClubFacilityLinks } = useUsasClubFacilityLinksData();
  const { clubApprovedMeetsState, postApprovedMeetRequest, putApprovedMeetRequest } = useClubMeetsData();
  const { meetState, getMeet } = useMeetData();
  const { getGlobalRoute } = useGlobalRoutesData();
  const { lscState, getLscs } = useLscData();
  const [displayMeetInfoFormState, setDisplayMeetInfoFormState] = useState(true);
  const [displayMeetSessionsFormState, setDisplayMeetSessionsFormState] = useState(false);
  const [displayMeetSessionsDetailFormState, setDisplayMeetSessionsDetailFormState] = useState(false);
  const [displayStaffFormState, setDisplayStaffFormState] = useState(false);
  const [meetInfoState, setMeetInfoState] = useState({});
  const { meetSessionsState, setMeetSessionsState, resetModalState } = useMeetSessionsData();
  const [facilityOptionsState, setFacilityOptionsState] = useState([{ id: Constants.DEFAULT_ID, name: '--' }]);
  const [state, setState] = useState({ error: '' });
  const { securityState } = useSecurityData();

   const { formState, errorState, setIsDirty, handleSubmit, updateFormState, onValueTextPairChanged, setFormState
  } = UseForm(getInitialFormState, submitFormCallback, validate);

  function submitFormCallback(infoFormState) {
    setState({ error: '' });
    setDisplayMeetInfoFormState(false);
    setDisplayMeetSessionsFormState(true);

    setMeetInfoState(infoFormState);
    setMeetSessionsState({
      ...meetSessionsState,
      formState: {
        ...meetSessionsState.formState,
        meetStartDate: meetInfoState.startDate ? formatDate(meetInfoState.startDate) : Constants.BLANK_DATE_STRING,
        meetEndDate: meetInfoState.endDate ? formatDate(meetInfoState.endDate) : Constants.BLANK_DATE_STRING
      }
    })
  }

  const handleSubmitProxy = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    await handleSubmit();
  };

  const onAddSession = () => {
    const prevHighestSessionOrderId = meetSessionsState.sortedMeetSessionArray?.length > 0 ? Math.max(...meetSessionsState.sortedMeetSessionArray?.map(o => o.sessionOrderId)) : 0;
    setMeetSessionsState({ ...meetSessionsState, formState: { ...meetSessionsState.formState, sessionOrderId: prevHighestSessionOrderId >= 0 ? prevHighestSessionOrderId + 1 : '' }, errorState: {} });
    setDisplayMeetSessionsFormState(false);
    setDisplayMeetSessionsDetailFormState(true);
  }

  const onEditSession = (e, session) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }
    setMeetSessionsState({ ...meetSessionsState, errorState: {}, formState: { ...meetSessionsState.formState, meetSessionId: session.meetSessionId } });
    setDisplayMeetSessionsFormState(false);
    setDisplayMeetSessionsDetailFormState(true);
  }

  const onDeleteSession = (e, meetSessionId) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }
    const meetSessionArrayCopy = JSON.parse(JSON.stringify(meetSessionsState.meetSessionArray));
    const selectedArrayIndex = meetSessionArrayCopy.findIndex(x => x.meetSessionId === meetSessionId);
    if (selectedArrayIndex > -1) {
      meetSessionArrayCopy.splice(selectedArrayIndex, 1);
      setMeetSessionsState({
        ...meetSessionsState,
        meetSessionArray: meetSessionArrayCopy,
        modalState: {
          displayPopUp: false,
          modalTitle: 'Delete Session?',
          meetSessionId: Constants.DEFAULT_ID,
          sessionOrderId: '',
          sessionTypeName: '',
          sessionDate: Constants.BLANK_DATE_STRING
        }
      })
    }
  }

  const onDeleteSessionClicked = (e, session) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    setMeetSessionsState({
      ...meetSessionsState,
      modalState: {
        ...meetSessionsState.modalState,
        displayPopUp: true,
        meetSessionId: session.meetSessionId,
        sessionOrderId: session.sessionOrderId,
        sessionTypeName: session.sessionType?.typeName,
        sessionDate: session.sessionDate
      }
    })
  };

  const onModalCanceled = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    resetModalState();
  };

  const onSessionsFormSubmit = () => {
    let dateError = false;
    if (meetSessionsState.meetSessionArray.length > 0) {
      const meetStartDate = new Date(meetSessionsState.formState?.meetStartDate);
      const meetEndDate = new Date(meetSessionsState.formState?.meetEndDate);

      for (let i = 0; i < meetSessionsState.meetSessionArray.length; i++) {
        const sessionDate = new Date(meetSessionsState.meetSessionArray[i].sessionDate);
        if (sessionDate < meetStartDate || sessionDate > meetEndDate) {
          dateError = true;
        }
      }

      // Validate there is a session for every day from meetStartDate through meetEndDate
      let missingDays = [];
      for (let d = new Date(meetStartDate); d <= meetEndDate; d.setDate(d.getDate() + 1)) {
        const dayStr = d.toISOString().slice(0, 10);
        const hasSession = meetSessionsState.meetSessionArray.some(session => {
          const sessionDayStr = new Date(session.sessionDate).toISOString().slice(0, 10);
          return sessionDayStr === dayStr;
        });
        if (!hasSession) {
          missingDays.push(dayStr);
        }
      }

      if (missingDays.length > 0) {
        setMeetSessionsState({ ...meetSessionsState, errorState: { error: `Add at least one session for each day of the meet. Missing: ${missingDays.join(', ')}` } });
      } else if (dateError === true) {
        setMeetSessionsState({ ...meetSessionsState, errorState: { error: `All Session Dates must be between the Meet Start Date and Meet End Date (${meetSessionsState?.formState.meetStartDate || ''} - ${meetSessionsState?.formState.meetEndDate || ''}).` } });
      } else {
        setDisplayStaffFormState(true);
      }
    }
  };

  const onRequestSubmit = () => {
    var meetObj = null;

    if (
        meetInfoState && 
        meetInfoState.meetId > 0  
        ) {
      meetObj = createMeetSanctionRequestForPut(meetState, meetInfoState);
      meetObj.meetId = meetInfoState.meetId;
      putApprovedMeetRequest(meetInfoState.meetId, meetInfoState.meetApprovalRequestId, meetObj);
    } else {
      meetObj = createMeetSanctionRequest(baseUsasClubState.objData.orgUnitId, meetInfoState);
      postApprovedMeetRequest(meetObj);
    }
  };


  const onCancelClicked = () => {
    navigate(NavLinks.CLUB_MEETS_APPROVED);
  };

  const onBackToMeetInfoClicked = () => {
    let dateError = false;
    if (meetSessionsState.meetSessionArray.length > 0) {
      const meetStartDate = new Date(meetSessionsState.formState.meetStartDate);
      const meetEndDate = new Date(meetSessionsState.formState.meetEndDate);

      for (let i = 0; i < meetSessionsState.meetSessionArray.length; i++) {
        const sessionDate = new Date(meetSessionsState.meetSessionArray[i].sessionDate);
        if (sessionDate < meetStartDate || sessionDate > meetEndDate) {
          dateError = true;
        }
      }

      if (dateError === true) {
        setMeetSessionsState({ ...meetSessionsState, errorState: { error: `All Session Dates must be between the Meet Start Date and Meet End Date (${meetSessionsState?.formState?.meetStartDate || ''} - ${meetSessionsState?.formState?.meetEndDate || ''}).` } });
      }
      else {
        setDisplayMeetSessionsFormState(false);
        setDisplayMeetInfoFormState(true);
      }
    }
    else {
      setMeetSessionsState({ ...meetSessionsState, errorState: { error: 'At least one Session is required.' } });
    }
  };

  const onBackToMeetSessionsFromDetail = () => {
    setDisplayMeetSessionsDetailFormState(false);
    setDisplayMeetSessionsFormState(true);
  };

  useEffect(() => {
    if (lscState.isLoading !== true && lscState.isLoaded !== true) {
      getLscs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (location.state?.meet) {
      const meetInfo = location.state.meet;

      getMeet(meetInfo.meetId);
      setMeetInfoState(meetInfo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  useEffect(() => {
    if (securityState.userInfo) {
      setFormState({
        ...formState,
        payorMemberId: securityState.userInfo.memberId
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [securityState.userInfo]);

  useEffect(() => {
    if (meetState.isObjLoaded === true && meetState.isObjLoading === false) {
      const sessionArray = [];
      for (let i = 0; i < meetState.objData.meetSession.length; i++) {
        const session = meetState.objData.meetSession[i];

        sessionArray.push({
          meetSessionId: session.meetSessionId,
          sessionOrderId: session.sessionOrderId,
          sessionTypeId: session.sessionTypeId,
          sessionType: { typeId: session.sessionTypeId, typeName: session.sessionType.typeName },
          sessionDate: session.sessionDate
        });
      }

      setMeetSessionsState({ ...meetSessionsState, meetSessionArray: sessionArray, sortedMeetSessionArray: sessionArray });

      setDisplayMeetInfoFormState(true);
      setDisplayMeetSessionsFormState(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetState]);

  useEffect(() => {
    if (baseUsasClubState.isObjLoading === false && baseUsasClubState.isObjLoaded === true
      && usasClubFacilityLinksState.isArrayLoaded === false && usasClubFacilityLinksState.isArrayLoading === false) {
      getUsasClubFacilityLinks(baseUsasClubState.objData.usasClubId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseUsasClubState, usasClubFacilityLinksState]);

  useEffect(() => {
    if (baseUsasClubState.isObjLoading === false && baseUsasClubState.isObjLoaded === true
      && usasClubFacilityLinksState.isArrayLoading === false && usasClubFacilityLinksState.isArrayLoaded === true) {
      setFacilityOptionsState(getFacilityAsOptions(usasClubFacilityLinksState.arrayData || []));
    }
  }, [baseUsasClubState, usasClubFacilityLinksState]);

  useEffect(() => {
    if (meetSessionsState.meetSessionArray?.length > 0) {
      //Sort grid data to display in ascending order
      const meetSessionArrayCopy = JSON.parse(JSON.stringify(meetSessionsState.meetSessionArray));
      meetSessionArrayCopy.sort(function (a, b) { return a.sessionOrderId - b.sessionOrderId });
      setMeetSessionsState({
        ...meetSessionsState,
        sortedMeetSessionArray: meetSessionArrayCopy,
        formState: {
          ...meetSessionsState.formState,
          meetStartDate: meetInfoState.startDate ? formatDate(meetInfoState.startDate) : Constants.BLANK_DATE_STRING,
          meetEndDate: meetInfoState.endDate ? formatDate(meetInfoState.endDate) : Constants.BLANK_DATE_STRING
        }
      });
    }
    else {
      setMeetSessionsState({
        ...meetSessionsState,
        sortedMeetSessionArray: [],
        formState: {
          ...meetSessionsState.formState,
          meetStartDate: meetInfoState.startDate ? formatDate(meetInfoState.startDate) : Constants.BLANK_DATE_STRING,
          meetEndDate: meetInfoState.endDate ? formatDate(meetInfoState.endDate) : Constants.BLANK_DATE_STRING
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetSessionsState.meetSessionArray, meetInfoState.startDate, meetInfoState.endDate]);

  useEffect(() => {
    if (clubApprovedMeetsState.isSaved === true) {
      navigate(NavLinks.CLUB_MEETS_APPROVED);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubApprovedMeetsState.isSaved]);

  useEffect(() => {
    if (formState.thisIsMe === true) {
      setFormState({
        ...formState,
        payorMemberId: securityState.userInfo.memberId
      });
    } else if (formState.thisIsMe === false) {
      setFormState({
        ...formState, 
        payorMemberId: ''
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState.thisIsMe]);

  useEffect(() => {
    if (clubApprovedMeetsState.isObjLoaded === true && clubApprovedMeetsState.objData.sanctionOrgUnitId === USAS_ORG_UNIT_ID) {

      if (clubApprovedMeetsState.objData.status === "Pending" && meetInfoState.payorMemberId === securityState.userInfo.memberId) {
        const paymentsRoute = getGlobalRoute('PAYMENTS_SHOPPING_CART');

        if (paymentsRoute && paymentsRoute.route) {
          navigateToCrossUiRoute(paymentsRoute.uIProjectName, paymentsRoute.route, navigate);
        }
      } else {
        navigate(NavLinks.CLUB_MEETS_APPROVED);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubApprovedMeetsState]);

  function getFacilityAsOptions(facilityArray) {
    const facilityOptions = [{ id: Constants.DEFAULT_ID, name: '--' }];
    if (facilityArray) {
      for (const facility of facilityArray) {
        if (facility.facility && facility.facility.hostMeet === true) {
          facilityOptions.push({ id: facility.facility.facilityId, name: facility.facility.facilityName });
        }
      }
    }
    return facilityOptions;
  }

  function createMeetSanctionRequestForPut(meetState, meetInfo) {
    const currentSessionArray = meetSessionsState.sortedMeetSessionArray;
    const oldSessionArray = meetState.objData.meetSession;

    for (let i = 0; i < oldSessionArray.length; i++) {
      const oldSession = oldSessionArray[i];
      const newSession = currentSessionArray.find(session => session.meetSessionId === oldSession.meetSessionId);

      if (newSession) {
        oldSessionArray[i].sessionTypeId = newSession?.sessionTypeId || oldSession.sessionTypeId;
        oldSessionArray[i].sessionDate = newSession?.sessionDate || oldSession.sessionDate;
      } else {
        oldSessionArray.splice(i, 1);
        i--;
      }
    }

    for (let i = 0; i < currentSessionArray.length; i++) {
      const newSession = currentSessionArray[i];
      const oldSession = oldSessionArray.find(session => session.meetSessionId === newSession.meetSessionId);

      const sessionObj = {};

      if (!oldSession) {
        sessionObj.sessionOrderId = newSession.sessionOrderId;
        sessionObj.sessionDate = newSession.sessionDate;
        sessionObj.sessionTypeId = newSession.sessionTypeId;
        sessionObj.meetId = meetState.objData.meetId;
        sessionObj.sessionType = null;

        oldSessionArray.push(sessionObj);
      }
    }

    const meetName = meetInfo.meetName || '';
    const meetYear =  meetInfo.startDate.substring(6, 10);
    const hasFourDigitPrefix = /^\d{4}/.test(meetName);

    const newMeetName = hasFourDigitPrefix
      ? `${meetYear}${meetName.substring(4)}`
      : meetName;
    
    return {
      meetId: meetState.objData.meetId,
      meetClassificationId: meetInfo.meetClassification[0].id,
      meetName: newMeetName,
      startDate: meetInfo.startDate,
      endDate: meetInfo.endDate,
      hostOrgUnitId: meetState.objData.hostOrgUnitId,
      facilityId: meetInfo.facilityId,
      meetAbbreviation: '',
      meetSoftwareId: meetInfo.meetSoftwareId,
      meetAnnouncementUrl: meetInfo.meetAnnouncementUrl || '',
      meetSanctionNumber: meetState.objData.meetSanctionNumber || '',
      meetClassification: meetState.objData.meetClassification,
      status: meetState.objData.status,
      workflowId: meetState.objData.workflowId,
      isUploadLocked: meetState.objData.isUploadLocked,
      meetResultsUploaded: meetState.objData.meetResultsUploaded,
      meetResultsDocUrl: meetState.objData.meetResultsDocUrl || '',
      isInWaterStart: meetInfo.isInWaterStart,
      depthRequirementMet: meetInfo.depthRequirementMet,
      sanctionOrgUnitId: meetInfo.isUsasSanctionMeet ? USAS_ORG_UNIT_ID : meetState.objData.sanctionOrgUnitId,
      geographicSanctionOrgUnitId: meetInfo.geographicFacilityLscOrgUnitId,
      meetFormatId: meetInfo.meetFormatId,
      payorPersonId: formState.payorPersonId,
      isFinalUploadFile: meetState.objData.isFinalUploadFile,
      meetCourse: meetInfo.meetCourse.map(
        (course, x) => { return { courseId: course.id, meetId: meetState.objData.meetId }; }),
      isUsasMeet: true,
      isFinaMeet: false,
      isInternationalSwimLeagueMeet: false,
      isNcaaMeet: false,
      meetSession: oldSessionArray,
      meetApprovalRequest: [
        { 
          meetApprovalRequestId: meetInfo.meetApprovalRequestId,
          competitionType: formState.competitionType, 
          openTypeAsApprovedMeetReason: formState.openTypeAsApprovedMeetReason || '',
          isOpenWaterCompetition: formState.isOpenWaterCompetition || false,
          isClubRequestingApproval: formState.isClubRequestingApproval || false,
          attestationSignature: formState.attestationSignature || '',
          attestationSignatureDate: formState.attestationSignatureDate || ''
        }
      ]
    };
  }

  function createMeetSanctionRequest(orgUnitId, meetInfo) {
    let meetSessionArrayCopy = JSON.parse(JSON.stringify(meetSessionsState.meetSessionArray));
    for (let i = 0; i < meetSessionArrayCopy.length; i++) {
      delete meetSessionArrayCopy[i].meetSessionId;
      meetSessionArrayCopy[i].sessionType = undefined;
    }

    const now = new Date();
    const currentDate = `${String(now.getMonth() + 1).padStart(2, '0')}/${String(now.getDate()).padStart(2, '0')}/${now.getFullYear()}`;
    const geographicLscCode = lscState.data.find(x => x.orgUnitId === meetInfo.geographicFacilityLscOrgUnitId)?.orgUnitCode || '';

    return {
      meetClassificationId: meetInfo.meetClassification[0].id,
      meetName: meetInfo.autogeneratedMeetYear + ' ' + baseUsasClubState.objData?.orgUnit?.parentOrgUnitCode + ' ' + meetInfo.meetName,
      startDate: meetInfo.startDate,
      endDate: meetInfo.endDate,
      hostOrgUnitId: orgUnitId,
      isUsasMeet: true,
      isFinaMeet: false,
      isInternationalSwimLeagueMeet: false,
      isNcaaMeet: false,
      meetCourse: meetInfo.meetCourse.map(course => { return { courseId: course.id }; }),
      facilityId: meetInfo.facilityId,
      isInWaterStart: meetInfo.isInWaterStart,
      depthRequirementMet: meetInfo.depthRequirementMet,
      meetFormatId: meetInfo.meetFormatId,
      payorPersonId: meetInfo.payorPersonId,
      meetAbbreviation: '',
      meetSoftwareId: meetInfo.meetSoftwareId,
      meetAnnouncementUrl: meetInfo.meetAnnouncementUrl,
      meetSanctionNumber: `AP-USA-${geographicLscCode}-${baseUsasClubState.objData?.orgUnit?.orgUnitCode}-`,
      sanctionOrgUnitId: meetInfo.isUsasSanctionMeet ? USAS_ORG_UNIT_ID : orgUnitId,
      geographicSanctionOrgUnitId: meetInfo.geographicFacilityLscOrgUnitId,
      isFinalUploadFile: false,
      status: Constants.WORKFLOW_STATUS_PENDING,
      meetSession: meetSessionArrayCopy,
      exemptFromPayment: 0,
      meetApprovalRequest: [
        { 
          competitionType: formState.competitionType, 
          openTypeAsApprovedMeetReason: formState.openTypeAsApprovedMeetReason || '',
          isOpenWaterCompetition: formState.isOpenWaterCompetition || false,
          isClubRequestingApproval: formState.isClubRequestingApproval || false,
          attestationSignature: formState.attestationSignature || '',
          attestationSignatureDate: currentDate
        }
      ]
    };
  }

  function getInitialFormState() {
    return {
      meetId: Constants.DEFAULT_ID,
      meetName: '',
      startDate: Constants.BLANK_DATE_STRING,
      endDate: Constants.BLANK_DATE_STRING,
      startDateOriginal: Constants.BLANK_DATE_STRING,
      endDateOriginal: Constants.BLANK_DATE_STRING,
      meetClassification: [],
      meetClassificationId: Constants.DEFAULT_ID,
      meetClassificationName: '',
      meetCourse: [],
      meetSoftwareId: Constants.DEFAULT_ID,
      meetSoftwareName: '',
      facilityId: Constants.DEFAULT_ID,
      facilityName: '',
      geographicFacilityLscOrgUnitId: Constants.DEFAULT_ID,
      geographicFacilityLscName: '',
      autogeneratedMeetYear: '',
      autogeneratedMeetLsc: '',
      meetAnnouncementUrl: '',
      meetAnnouncementFileName: '',
      isUsasSanctionMeet: true,
      meetFormatId: Constants.DEFAULT_ID,
      meetFormatName: '',
      isInWaterStart: false,
      depthRequirementMet: false,
      attestationSignature: '',
      meetSanctionAttestation: '',
      hasUsasSanctionAccess: false,
      payorMemberId: '',
      payorPersonId: Constants.DEFAULT_ID,
      pilotStartDate: Constants.BLANK_DATE_STRING,
      pilotEndDate: Constants.BLANK_DATE_STRING,
      thisIsMe: false,
      status: '',
      competitionType: '',
      competitionTypeName: '',
      openTypeAsApprovedMeetReason: '',
      isOpenWaterCompetition: false,
      isClubRequestingApproval: false
    };
  }

  return {
    isSaving: clubApprovedMeetsState.isSaving,
    isLoading: environmentVariableState.isLoading || meetState.isObjLoading || baseUsasClubState.isObjLoading || usasClubFacilityLinksState.isArrayLoading,
    formState,
    errorState,
    setFormState,
    setIsDirty,
    handleSubmitProxy,
    onFormValueChanged: updateFormState,
    onValueTextPairChanged,
    meetState,
    meetInfoState,
    meetSessionsState,
    displayMeetInfoFormState,
    displayMeetSessionsFormState,
    displayMeetSessionsDetailFormState,
    displayStaffFormState,
    facilityOptionsState,
    orgUnitId: baseUsasClubState.objData.orgUnitId || '',
    onSessionsFormSubmit,
    onRequestSubmit,
    onAddSession,
    onEditSession,
    onDeleteSession,
    onDeleteSessionClicked,
    onModalCanceled,
    onBackToMeetInfoClicked,
    onBackToMeetSessionsFromDetail,
    onCancelClicked,
    state,
    setState,
    OFFICIAL_ORG_ROLE_ID,
    OFFICIALS_ROLES_ROLE_GROUP_ID,
    hasUsasSanctionAccess: baseUsasClubState.objData?.hasUsasSanctionAccess || false
  };
};

export default UseClubMeetApprovedRequest;